import {
  type InputHTMLAttributes,
  type Ref,
  forwardRef,
  useEffect,
  useId,
  useState,
} from "react";

/**
 * ボタンの名前。表示している間も変えず、今の状態は aria-pressed だけで伝える
 * （名前を「隠す」に替えると、「パスワードを隠す、オン」と逆の意味に読み上げられるため）。
 */
export const SHOW_PASSWORD_LABEL = "パスワードを表示";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  /**
   * 表示切り替えのボタン（目のマーク）を付けるか。false（既定）なら、今までの type="password" の欄をそのまま出す。
   * 患者の SAS の画面（Issue #473）だけが true にする。
   */
  revealable?: boolean;
  /**
   * サーバーへ送っている間は true。true になったら隠す（送り始めたら、表示したままにしない）。
   * 画面の検証で止まって送らなかったとき（確認用の不一致など）は隠さない。打ち間違いを探すために表示しているため。
   */
  sending?: boolean;
};

const iconProps = {
  width: 20,
  height: 20,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  focusable: false,
} as const;

/** 目のマーク（いまは隠している。押すと表示する）。 */
const EyeIcon = () => (
  <svg {...iconProps}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

/** 斜線を引いた目のマーク（いまは表示している。押すと隠す）。 */
const EyeOffIcon = () => (
  <svg {...iconProps}>
    <path d="M17.9 17.9A10.4 10.4 0 0 1 12 19c-6.4 0-10-7-10-7a17.6 17.6 0 0 1 4.1-4.9" />
    <path d="M9.9 5.2A9.7 9.7 0 0 1 12 5c6.4 0 10 7 10 7a17.7 17.7 0 0 1-2.2 3.2" />
    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    <path d="m2 2 20 20" />
  </svg>
);

/**
 * パスワードの入力欄。revealable なら、右端に表示切り替えのボタン（目のマーク）を重ねる。
 * - 表示の状態はこの部品の中だけで持つ（値は親のもの）。切り替えても値とフォーカスの扱いは変わらない。
 * - sending が true になったら隠す。画面を移して部品が消えれば、次に出すときも隠した状態から始まる。
 * - ボタンは type="button"（送信しない）。名前は「パスワードを表示」のままで、今の状態は aria-pressed で伝える。
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { revealable = false, sending = false, className, ...input }: Props,
  ref: Ref<HTMLInputElement>,
) {
  const [visible, setVisible] = useState(false);
  const inputId = useId();
  const id = input.id ?? inputId;

  // 送り始めたら隠す（送信に失敗して同じ画面に残っても、表示したままにしない）
  useEffect(() => {
    if (sending) setVisible(false);
  }, [sending]);

  if (!revealable) {
    return <input {...input} ref={ref} type="password" className={className} />;
  }

  const shown = visible;
  return (
    <div className="relative">
      <input
        {...input}
        id={id}
        ref={ref}
        type={shown ? "text" : "password"}
        // 表示している間に、補正や先読みで入力が書き換わらないようにする
        {...(shown
          ? { autoCapitalize: "off", autoCorrect: "off", spellCheck: false }
          : {})}
        className={`${className ?? ""} pr-12`}
      />
      <button
        type="button"
        aria-label={SHOW_PASSWORD_LABEL}
        aria-pressed={shown}
        aria-controls={id}
        disabled={input.disabled}
        onClick={() => setVisible((current) => !current)}
        className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-md text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-brandBlue disabled:opacity-50"
      >
        {shown ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
});
